import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { getCookie, setCookie } from "utils/v3/CookieUtil";
import { doGreatInitialApiScrape } from "../../../../apis/authApi";
import {
  setUserAgreementHtmlFromApiResponse,
  setUserAgreementVersionAcceptanceStates
} from "../../../../store/reducers/termsOfService/TermsOfServiceSlice";
import {
  setNotificationSettings,
  setSpouseInformation,
  setGreatInitialApiScrapeIsHappening,
  updateFieldsInUserPersonalInformation,
  setGreatInitialApiScrapeIsComplete,
  setHasNewResultsToView
} from "../../../../store/reducers/user/UserAccountSlice";
import {
  setAvailableKitsToOrder,
  setExistingOrders,
  setRiskAssessments
} from "../../../../store/reducers/order/OrderInformationSlice";
import { updateSurveyAnswersForAllPreviousSurveys } from "../../../../store/reducers/survey/SurveySlice";
import Mixpanel from "../../../../utils/mixpanel";
import { PATHS } from "../../../../constants/pathConstants";
import { openGLSnackbar } from "../../../../store/reducers/snackbar/openGLSnackbarSlice";
import LoadingPage from "components/v3/pages/LoadingPage";
import { MEMBER_ACCOUNT_ACTIVE_STATUSES } from "constants/v3/generalConstants";
import { getDocument } from "../../../../apis/v3/orderApi";
import { getTestKitMetadata } from "../TestResultsPage/testLanguage";

const ScrapeApiPage = () => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims
  } = useAuth0();
  const dispatch = useDispatch();
  const history = useHistory();

  //This feels gross, but there are a few reasons we need the useEffect loop to
  //  kick over again in during the back and forth redirects required by the auth0 flow
  //  this lets us tell the effect to run agian on the next page load.
  const [refreshLoopCount, setRefreshLoopCount] = useState(0);

  const [scraping, setScraping] = useState(false);

  // Trash this as soon as the glid gets moved to app_metadata
  //  instead of user_metadata
  function findValueByKey(obj, key) {
    let foundValue = null;
    const search = (obj, key) => {
      Object.entries(obj).find(([prop, value]) => {
        if (prop === key) {
          foundValue = value;
          return true;
        } else if (typeof value === "object") {
          search(value, key);
        }
      });
    };
    search(obj, key);
    return foundValue;
  }

  useEffect(() => {
    const getToken = async () => {
      await getAccessTokenSilently({ cacheMode: "off" })
        .then((token) => {
          getIdTokenClaims().then(async (userDataFromAuth0Token) => {
            const userGlid = findValueByKey(
              userDataFromAuth0Token,
              "glMemberId"
            );
            const glProgramId = findValueByKey(
              userDataFromAuth0Token,
              "glProgramId"
            );
            const glMemberStatus = findValueByKey(
              userDataFromAuth0Token,
              "glMemberStatus"
            );
            dispatch(
              updateFieldsInUserPersonalInformation({
                glMemberId: userGlid,
                email: userDataFromAuth0Token.email,
                glProgramId: glProgramId,
                isMemberActive: MEMBER_ACCOUNT_ACTIVE_STATUSES.includes(
                  glMemberStatus?.replace(/[,\/\-\s']/g, "").toUpperCase()
                )
              })
            );
            Mixpanel.identify(userGlid);
            setCookie("accessToken", token);
            setRefreshLoopCount(refreshLoopCount + 1);
          });
        })
        .catch((error) => {
          if (
            [
              "invalid_grant",
              "missing_refresh_token",
              "consent_required"
            ].includes(error.error)
          ) {
            //access token reported not got, but that's bunk. Ignoring
            setRefreshLoopCount(refreshLoopCount + 1);
            //loginWithRedirect();
          } else {
            Mixpanel.track(
              "Login callback page was unable to get access token"
            );
            throw error;
          }

          //history.push(PATHS.LOG_OUT);
        });
    };

    const scrapeApi = async () => {
      if (!scraping) {
        setScraping(true);

        await dispatch(setGreatInitialApiScrapeIsHappening(true));

        try {
          // I have consolidated a lot of calls from around the app that caused slow page loads
          //  and inconsistent state in the store based on which pages you visited first. There had
          //  not been enough discipline in having pages get what they need and store what they changed.
          // This makes the login spinner run a little longer, and might get a few things some member
          //  sessions won't need, but it's not a lot of stuff, and this will at least be consistent.
          // This should all be audited and paired down / distributed again thoughtfully.
          doGreatInitialApiScrape()
            .then(async (responses) => {
              const [
                memberDataResponse,
                userAgreementVersionAcceptanceResponse,
                userAgreementHtmlResponse,
                spouseResponse,
                existingKitOrdersResponse,
                testKitIdForEachTestTypeResponse,
                gettingStartedSurveyResponse,
                riskAssessmentResponse
              ] = responses;

              // Remove this when the member endpoint stops serving this key
              const {
                isMemberActive,
                ...memberDataWithoutStatus
              } = memberDataResponse.data;
              dispatch(
                updateFieldsInUserPersonalInformation(memberDataWithoutStatus)
              );
              dispatch(
                setUserAgreementVersionAcceptanceStates(
                  userAgreementVersionAcceptanceResponse
                )
              );

              dispatch(
                setUserAgreementHtmlFromApiResponse(userAgreementHtmlResponse)
              );

              dispatch(
                setNotificationSettings(userAgreementVersionAcceptanceResponse)
              );

              dispatch(setSpouseInformation(spouseResponse.data.data));

              // Get test kit metadata from the API response and look it up in testLanguage.js
              const testKitIds = testKitIdForEachTestTypeResponse.data || {};
              const items = [];

              // Process each test type from the API response
              Object.values(testKitIds).forEach((testTypeData) => {
                const testId = testTypeData?.testId;
                if (!testId) return;

                const metadata = getTestKitMetadata(testId);
                if (!metadata) {
                  console.warn(`No metadata found for test ID: ${testId}`);
                  return;
                }

                const item = {
                  glTestType: metadata.glTestType,
                  test_uuid: metadata.test_uuid,
                  name: metadata.name,
                  description: metadata.shortDescription,
                  header: metadata.header,
                  test_name: metadata.test_name,
                  purpose: metadata.purpose,
                  lab: metadata.lab,
                  order: metadata.order,
                  ...(metadata.chipInformation && {
                    chipInformation: metadata.chipInformation
                  })
                };

                items.push(item);
              });
              dispatch(setAvailableKitsToOrder(items));
              dispatch(setExistingOrders(existingKitOrdersResponse));

              dispatch(setRiskAssessments(riskAssessmentResponse.data));

              const orders = existingKitOrdersResponse.data.orders || [];

              // Get all GHS orders with documents
              const ghsOrdersWithDocs = orders.filter(
                (order) =>
                  order.glTestType === "GHS" && order.documentIds?.length > 0
              );

              // Get all document IDs from GHS orders
              const ghsDocumentIds = ghsOrdersWithDocs.flatMap(
                (order) => order.documentIds
              );

              if (ghsDocumentIds.length === 0) {
                dispatch(setHasNewResultsToView(false));
              } else {
                // Get metadata for each GHS document
                const documentMetadataPromises = ghsDocumentIds.map((docId) =>
                  getDocument(docId)
                );

                try {
                  const documentMetadataResponses = await Promise.all(
                    documentMetadataPromises
                  );

                  // Check if any GHS documents have not been viewed and were received after June 5, 2025
                  const hasUnviewedGhsDocuments = documentMetadataResponses.some(
                    (response) => {
                      const { viewCount, qcResultDate } = response.data;
                      // Skip documents before June 5, 2025
                      if (
                        qcResultDate &&
                        new Date(qcResultDate) < new Date("2025-06-05")
                      ) {
                        return false;
                      }
                      // Consider a document unviewed if viewCount is missing, null, or 0
                      return (
                        viewCount === undefined ||
                        viewCount === null ||
                        viewCount === 0
                      );
                    }
                  );

                  dispatch(setHasNewResultsToView(hasUnviewedGhsDocuments));
                } catch (error) {
                  console.error("Error fetching document metadata:", error);
                  dispatch(setHasNewResultsToView(false));
                }
              }

              dispatch(
                updateSurveyAnswersForAllPreviousSurveys(
                  gettingStartedSurveyResponse
                )
              );

              dispatch(setGreatInitialApiScrapeIsHappening(false));
              dispatch(setGreatInitialApiScrapeIsComplete(true));
              setScraping(false);

              // This is a gross stopgap
              setTimeout(() => {
                Mixpanel.track("Login successful");
                history.push(PATHS.LANDING_PAGE);
              }, 5000);
            })
            .catch((err) => {
              Mixpanel.track("Storing data from login api calls crashed");
              dispatch(setGreatInitialApiScrapeIsHappening(false));
              dispatch(setGreatInitialApiScrapeIsComplete(true));
              dispatch(
                openGLSnackbar({
                  variant: "error",
                  header: "Error logging in",
                  subText:
                    "Sorry, something went wrong when we tried to log you in. Please try again."
                })
              );
              window.location.href = `${PATHS.LOG_OUT}`;
            });
        } catch {
          Mixpanel.track("Login api calls crashed");
          dispatch(setGreatInitialApiScrapeIsHappening(false));
          dispatch(setGreatInitialApiScrapeIsComplete(true));
          dispatch(
            openGLSnackbar({
              variant: "error",
              header: "Error logging in",
              subText:
                "Sorry, something went wrong when we tried to log you in. Please try again."
            })
          );
          window.location.href = `${PATHS.LOG_OUT}`;
        }
      }
    };

    if (!getCookie("accessToken")) {
      getToken();
    } else {
      getIdTokenClaims().then(async (userDataFromAuth0Token) => {
        const userGlid = findValueByKey(userDataFromAuth0Token, "glMemberId");
        if (userGlid) {
          scrapeApi();
        } else {
          getToken();
        }
      });
    }
  }, [isAuthenticated, refreshLoopCount]);

  return (
    <div>
      <LoadingPage message={"Retrieving your account..."} />
    </div>
  );
};

export default ScrapeApiPage;
